import { useCallback, useEffect, useMemo, useRef } from 'react';
import { GetStaticProps } from 'next';
import { useAtom, useSetAtom } from 'jotai';
import debounce from 'lodash.debounce';
import { useTranslation } from 'next-i18next';
import { useIntercom } from 'react-use-intercom';

import { SkeletonEmployerCardListLoading, SkeletonTextLoading } from '@restworld/ui-components';
import { cn, useIsFreemiumUser } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';

import EmployerPrimaryLayout from '../../../layouts/employer-primary-layout';
import { getServerTranslations } from '../../../get-server-side-translations';
import useEmployerJobListings from '../../../components/job-listing/hooks/use-employer-job-listing';
import { useOpenNewJobPosition } from '../../../components/job-listing/hooks/use-open-new-job-position';
import {
  employerJobListingSearchQueryAtom,
  employerJobListingSortValueAtom,
  employerJobListingJpStatusFilterAtom,
  employerJobListingRestaurantNameFilterAtom,
  employerJobListingLocationFilterAtom,
  employerJobListingOccupationFilterAtom,
} from '../../../components/job-listing/employer-job-listing.atoms';
import EmployerJobListingFilters, {
  computeJobListingFiltersCount,
  IEmployerFilterForm,
} from '../../../components/job-listing/employer-job-listing-filters';
// import useEmployerJobListingSortMenuList from '../../../components/job-listing/hooks/use-employer-job-listing-sort-menu-list';
import EmployerJobListingCardsList from '../../../components/job-listing/employer-job-listing-cards-list';
import FreemiumUserJPPageModal, {
  isFreemiumUserJpPageModalOpenAtom,
} from '../../../components/job-listing/freemium-user-jp-page-modal';
import UnpaidInvoicesModal from '../../../components/job-position-onboarding/modals/unpaid-invoices-modal';
import { InvoiceBlockPage } from '../../../components/invoice-block-page';

export const baseUrl = process.env.NEXT_PUBLIC_BASE_API_URL;

function JobListingPage() {
  const { t: translateCommon } = useTranslation('common');
  const { startTour } = useIntercom();
  const { user } = useAuth();
  const [employerJobListingSearchQuery, setEmployerJobListingSearchQuery] = useAtom(employerJobListingSearchQueryAtom);
  const [employerJobListingSortValue /*setEmployerJobListingSortValue */] = useAtom(employerJobListingSortValueAtom);
  const [jpStatus, setJpStatus] = useAtom(employerJobListingJpStatusFilterAtom);
  const [restaurantName, setRestaurantName] = useAtom(employerJobListingRestaurantNameFilterAtom);
  const [location, setLocation] = useAtom(employerJobListingLocationFilterAtom);
  const [occupationId, setOccupationId] = useAtom(employerJobListingOccupationFilterAtom);
  // const [, setShowContactRestworldManagerModal] = useAtom(showContactRestWorldManagerModalAtom);
  const filterFormRef = useRef<IEmployerFilterForm | null>(null);
  const isFreemiumUser = useIsFreemiumUser();
  const setIsFreemiumUserJpPageModalOpen = useSetAtom(isFreemiumUserJpPageModalOpenAtom);

  // Gating for the "Apri nuova posizione" CTA (invoices, ATS, analytics) lives in the hook,
  // which is shared with the homepage entry point.
  const {
    openNewJobPosition,
    isDisabled: isAddButtonDisabled,
    disabledTooltip: addButtonDisabledTooltip,
    isBusy: isCheckingOpenNewJobPosition,
    isCheckingInvoices: isCheckingInvoiceStatus,
    invoiceStatus,
  } = useOpenNewJobPosition({ source: 'job_positions_page', checkInvoicesOnMount: true });

  // const sortMenuList = useEmployerJobListingSortMenuList();
  const { jobPositionItems, hits, query, hasNextPage, loading } = useEmployerJobListings({
    searchVector: employerJobListingSearchQuery,
    orderBy: [employerJobListingSortValue],
    status: jpStatus?.length ? jpStatus[0] : undefined,
    restaurantName,
    location,
    occupationId,
  });
  const totalSearchResults = useMemo(
    () =>
      loading ? (
        <SkeletonTextLoading width="medium" className="mt-2" />
      ) : (
        `${hits || 0} ${hits === 1 ? translateCommon('job_position') : translateCommon('job_positions')}`
      ),
    [hits, loading, translateCommon]
  );

  const jobListingFiltersCount = useMemo(
    () => computeJobListingFiltersCount({ restaurantName, jpStatus, location, occupationId }),
    [restaurantName, jpStatus, location, occupationId]
  );

  const searchProps = useMemo(
    () => ({
      placeholder: 'what_job_position_are_you_looking_for',
      onChange: debounce((value) => setEmployerJobListingSearchQuery(value), 500),
    }),
    [setEmployerJobListingSearchQuery]
  );
  const noResultsFoundProps = useMemo(
    () => ({
      title: translateCommon('no_jobs_found_title'),
      content: translateCommon('no_jobs_found_content'),
      foundNoResults: !query.isLoading && jobPositionItems?.length === 0,
    }),
    [jobPositionItems?.length, query.isLoading, translateCommon]
  );
  const filterProps = useMemo(
    () => ({
      element: <EmployerJobListingFilters ref={filterFormRef} />,
      filterCount: jobListingFiltersCount,
      formRef: filterFormRef,
    }),
    [jobListingFiltersCount]
  );
  const loadMoreBtnProps = useMemo(
    () => ({
      onClick: () => query.fetchNextPage(),
      loading: query.isFetchingNextPage,
      hasNextPage,
    }),
    [hasNextPage, query]
  );

  const onInfoIconClick = useCallback(() => startTour(599323), [startTour]);

  const primaryLayoutProps = useMemo(
    () => ({
      totalSearchResults,
      searchProps,
      addButtonTitle: 'open_new_position',
      pageTitle: 'job_positions_key',
      noResultsFoundProps,
      filterProps,
      title: 'job_positions_title',
      onAddButtonClick: openNewJobPosition,
      loadMoreBtnProps,
      loading: loading || isCheckingInvoiceStatus,
      isResultAndFilterElAlwaysOnSameLine: true,
      onInfoIconClick,
      addButtonLoading: false,
      addButtonDisabled: isAddButtonDisabled || isCheckingOpenNewJobPosition,
      addButtonTooltip: addButtonDisabledTooltip,
    }),
    [
      filterProps,
      loadMoreBtnProps,
      loading,
      noResultsFoundProps,
      openNewJobPosition,
      onInfoIconClick,
      searchProps,
      totalSearchResults,
      isCheckingInvoiceStatus,
      isAddButtonDisabled,
      isCheckingOpenNewJobPosition,
      addButtonDisabledTooltip,
    ]
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const jpStatus = urlParams.get('jpStatus');
    const location = urlParams.get('location');
    const restaurantName = urlParams.get('restaurantName');
    const occupationId = urlParams.get('occupationId');
    if (jpStatus) setJpStatus([jpStatus]);
    if (location) setLocation(location);
    if (restaurantName) setRestaurantName(restaurantName);
    if (occupationId) setOccupationId(occupationId);
  }, [setJpStatus, setLocation, setRestaurantName, setOccupationId]);

  useEffect(() => {
    if (isFreemiumUser) setIsFreemiumUserJpPageModalOpen(true);
  }, [isFreemiumUser, setIsFreemiumUserJpPageModalOpen]);

  // Show InvoiceBlockPage if block_completely is true (only for PPU business model users)
  if (user?.using_ppu_business_model === true && invoiceStatus?.block_completely) {
    return <InvoiceBlockPage />;
  }

  return (
    <>
      <EmployerPrimaryLayout {...primaryLayoutProps}>
        {loading || isCheckingInvoiceStatus ? (
          <SkeletonEmployerCardListLoading />
        ) : (
          <CardsContainer>
            <EmployerJobListingCardsList jobPositionItems={jobPositionItems} />
          </CardsContainer>
        )}
      </EmployerPrimaryLayout>
      <FreemiumUserJPPageModal />
      <UnpaidInvoicesModal />
    </>
  );
}

export default JobListingPage;

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const translation = await getServerTranslations(locale, [
    'common',
    'job-positions-page',
    'jp-onboarding',
    'referral-page',
  ]);
  return {
    props: {
      ...translation,
    },
  };
};

const CardsContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('grid grid-cols-1 gap-4', className)} {...props} />
);
